import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Shared dismissal lifecycle for slide-in panels.
 *
 * WHY this hook exists — il portait le cycle de vie commun aux DEUX panneaux
 * latéraux (aperçu de pièce et viewer de jurisprudence), dont les données étaient
 * et restent séparées. Les deux ont fusionné en un seul panneau à onglets le
 * 2026-09-15, donc un seul consommateur aujourd'hui : `DocumentPreviewPanel`.
 * Le hook reste extrait — Escape et le changement de route sont une politique de
 * coque, pas un détail de ce composant, et `BordereauPanel` comme la dialog de
 * renumérotation raisonnent déjà en fonction d'elle.
 *
 * Behaviour:
 *   - Escape key → calls `close()` when `isOpen === true`.
 *   - Route change (pathname only) → calls `close()` when `isOpen === true`.
 *     Using a ref for prevPath avoids re-registering the keydown listener on every
 *     navigation, while still closing when the user leaves the current page.
 */
export function useDismissiblePanel(isOpen: boolean, close: () => void): void {
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  useEffect(() => {
    if (prevPath.current !== location.pathname && isOpen) close();
    prevPath.current = location.pathname;
  }, [location.pathname, isOpen, close]);
}
